import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { CreditCard } from 'lucide-react';
import { createLoanSchema, type CreateLoanInput, type ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogOverlay } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddLoanModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const { data: laConfig } = useQuery({
    queryKey: ['payroll', 'la-config'],
    queryFn: () => payrollService.getLAConfig(),
  });

  const maxLoanAmount  = laConfig ? parseFloat(laConfig.max_loan_amount)  : 500000;
  const maxLoanEmis    = laConfig ? laConfig.max_loan_emis                : 24;
  const defaultRate    = laConfig ? parseFloat(laConfig.default_interest_rate) : 0;
  const reasonRequired = laConfig?.require_loan_reason ?? false;

  const { data: staffData, isLoading: staffLoading, isError: staffError } = useQuery({
    queryKey: ['staff', { limit: 100 }],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateLoanInput>({
    resolver: zodResolver(createLoanSchema),
    defaultValues: { interest_rate: defaultRate, start_date: today },
  });

  const amount    = useWatch({ control, name: 'amount' });
  const numEmis   = useWatch({ control, name: 'num_emis' });
  const startDate = useWatch({ control, name: 'start_date' });
  const userId    = useWatch({ control, name: 'user_id' });

  useEffect(() => {
    if (amount && numEmis && numEmis > 0) {
      setValue('emi_amount', parseFloat((Number(amount) / Number(numEmis)).toFixed(2)));
    }
  }, [amount, numEmis, setValue]);

  const create = useMutation({
    mutationFn: (data: CreateLoanInput) => payrollService.createLoan(data),
    onSuccess: () => {
      toast.success('Loan created successfully');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'loans'] });
      reset();
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay />
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4 text-teal-600" />
            Add Loan
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="loan_user_id">Employee *</Label>
            <Select
              value={userId ?? ''}
              onValueChange={(v) => setValue('user_id', v, { shouldValidate: true })}
              disabled={staffLoading}
            >
              <SelectTrigger id="loan_user_id">
                <SelectValue placeholder={staffLoading ? 'Loading employees…' : 'Select employee'} />
              </SelectTrigger>
              <SelectContent>
                {staffData?.data.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} {s.employee_code ? `(${s.employee_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {staffError && (
              <p className="text-xs text-amber-600">Failed to load employees — check permissions or network.</p>
            )}
            {!staffLoading && !staffError && (staffData?.data.length ?? 0) === 0 && (
              <p className="text-xs text-muted-foreground">No active employees found. Create staff members first.</p>
            )}
            {errors.user_id && <p className="text-xs text-red-500">{errors.user_id.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="loan_amount">
                Amount (₹) * (Max: ₹{maxLoanAmount.toLocaleString('en-IN')})
              </Label>
              <Input
                id="loan_amount"
                type="number"
                min="1"
                max={maxLoanAmount}
                {...register('amount')}
              />
              {errors.amount && <p className="text-xs text-red-500">{errors.amount.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="num_emis">Number of EMIs * (Max: {maxLoanEmis})</Label>
              <Input
                id="num_emis"
                type="number"
                min="1"
                max={maxLoanEmis}
                defaultValue={1}
                {...register('num_emis')}
              />
              {errors.num_emis && <p className="text-xs text-red-500">{errors.num_emis.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="emi_amount">EMI Amount (₹)</Label>
              <Input id="emi_amount" type="number" step="0.01" readOnly className="bg-muted" {...register('emi_amount')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="interest_rate">Interest Rate (% p.a.)</Label>
              <Input
                id="interest_rate"
                type="number"
                step="0.01"
                min="0"
                defaultValue={defaultRate}
                {...register('interest_rate')}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="start_date">Start Date</Label>
            <DatePicker id="start_date" value={startDate ?? ''} onChange={(v) => setValue('start_date', v)} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="loan_reason">
              Reason {reasonRequired ? '*' : ''}
            </Label>
            <Textarea
              id="loan_reason"
              placeholder="Reason for loan…"
              rows={2}
              required={reasonRequired}
              {...register('reason')}
            />
            {reasonRequired && (
              <p className="text-xs text-muted-foreground">Required by your organisation's policy.</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add Loan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
